import { useState, type FormEvent } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PERMISSIONS, type AdminUserView, type AdminWithTemporaryPassword } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { TextField } from '@/components/ops/Forms';
import { LoadMore, PageHeader, Section } from '@/components/ops/Layout';
import { ConfirmDialog, Modal, RevealOnce } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { formatWhen } from './AccountsPage';

const ROLES = [
  { value: 'owner', label: 'Owner', hint: 'everything: tenant setup, money, admins' },
  { value: 'analyst', label: 'Analyst', hint: 'reads all but the admin list, exports, works leads' },
  { value: 'viewer', label: 'Viewer', hint: 'dashboards, invoices, messaging, servers — no plans, costs, names or exports' },
];
const roleLabel = (role: string) => ROLES.find((r) => r.value === role)?.label ?? role;

const ACTION_WORDS: Record<string, string> = {
  'admin.created': 'added',
  'admin.renamed': 'renamed',
  'admin.role_changed': 'changed the role of',
  'admin.disabled': 'disabled',
  'admin.enabled': 'enabled',
  'admin.password_reset': 'reset the password of',
  'admin.unlocked': 'unlocked',
  'admin.password_changed': 'changed their own password',
  'setup.created': 'started setting up the tenant',
  'setup.retried': 'retried the setup of',
  'setup.step_retried': 'retried a setup step of',
  'setup.cancelled': 'cancelled the setup of',
  'tenant.owner_invitation_resent': 'resent the owner invitation of',
  'setup.password_revealed': 'revealed a generated owner password',
  'setup_catalog.replaced': 'replaced the setup steps of',
  'environment.created': 'added the environment',
  'environment.updated': 'changed the environment',
  'environment.key_set': 'set the internal key of',
  'import_key.issued': 'issued the import key',
  'import_key.revoked': 'revoked the import key',
  'replica.cleared': 'cleared the saved copy of the tenant list',
  'tenant.deletion_requested': 'asked to delete the tenant',
  'tenant.deletion_approved': 'approved deleting the tenant',
  'tenant.deletion_confirmed': 'confirmed deleting the tenant',
  'tenant.deletion_rejected': 'stopped the deletion of the tenant',
  'tenant.discard_started': 'started removing what a cancelled setup created for',
};

/** One admin's state in words — the thing to act on first. */
export function adminStatus(admin: Pick<AdminUserView, 'enabled' | 'locked_until' | 'must_change_password' | 'last_login_at'>):
  { label: string; tone: 'success' | 'warning' | 'danger' | 'muted' } {
  if (!admin.enabled) return { label: 'Disabled', tone: 'muted' };
  if (admin.locked_until) return { label: `Locked until ${formatTimestamp(admin.locked_until)}`, tone: 'danger' };
  if (admin.must_change_password) return { label: admin.last_login_at ? 'Must choose a new password' : 'Invited — not signed in yet', tone: 'warning' };
  return { label: 'Active', tone: 'success' };
}

type Pending =
  | { kind: 'toggle'; admin: AdminUserView }
  | { kind: 'reset'; admin: AdminUserView }
  | { kind: 'role'; admin: AdminUserView; role: string };

/**
 * Console admins (2026-09-30): who can sign in to this console, with which role. Owners add admins (a temporary password
 * is shown once and must be changed at first sign-in), change roles, disable, reset passwords and unlock. The server
 * refuses changes to your own role or status and never lets the last owner go; every change is listed below.
 */
export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.adminsManage);
  const admins = useQuery({ queryKey: ['admins'], queryFn: api.admins.list, retry: false });
  const audit = useInfiniteQuery({
    queryKey: ['admins', 'audit'],
    queryFn: ({ pageParam }) => api.admins.audit(pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.next_before ?? undefined,
    retry: false,
  });

  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [secret, setSecret] = useState<AdminWithTemporaryPassword | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admins'] });

  const update = useMutation({
    mutationFn: (p: Pending): Promise<AdminUserView | AdminWithTemporaryPassword> =>
      p.kind === 'reset'
        ? api.admins.resetPassword(p.admin.id)
        : api.admins.update(p.admin.id, p.kind === 'role' ? { role: p.role } : { enabled: !p.admin.enabled }),
    onSuccess: (result) => {
      setPending(null);
      if ('temporary_password' in result) setSecret(result);
      void refresh();
    },
  });
  const unlock = useMutation({ mutationFn: (id: string) => api.admins.unlock(id), onSuccess: () => void refresh() });

  return (
    <RequirePermission permission={PERMISSIONS.adminsRead} what="Console admins">
      <PageHeader
        title="Console admins"
        subtitle="Who can sign in to this console, and with which role."
        actions={canManage && <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>Add an admin</button>}
      />

      <Section title="Admins" meta={admins.data ? `${admins.data.admins.length} admin${admins.data.admins.length === 1 ? '' : 's'}` : undefined} flush>
        <QueryState query={admins}>
          {(data) => (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Name</th><th>Role</th><th>Status</th><th>Last sign-in</th>{canManage && <th />}</tr>
                </thead>
                <tbody>
                  {data.admins.map((a) => {
                    const status = adminStatus(a);
                    return (
                      <tr key={a.id}>
                        <td>
                          <span style={{ fontWeight: 600 }}>{a.display_name}</span>
                          {a.is_you && <span className="chip chip-info" style={{ marginInlineStart: 6 }}>you</span>}
                          <div className="t-caption muted">{a.email}</div>
                        </td>
                        <td>
                          {canManage && !a.is_you ? (
                            <span className="cell-select">
                              <Select label={`Role of ${a.display_name}`} inline value={a.role} searchable={false}
                                onChange={(role) => role !== a.role && setPending({ kind: 'role', admin: a, role })}
                                options={ROLES} />
                            </span>
                          ) : roleLabel(a.role)}
                        </td>
                        <td><StatusChip status={status.label} tone={status.tone} /></td>
                        <td className="t-caption" title={a.last_login_at ? formatTimestamp(a.last_login_at) : undefined}>
                          {a.last_login_at ? formatWhen(a.last_login_at) : <span className="subtle">never</span>}
                        </td>
                        {canManage && (
                          <td className="row" style={{ justifyContent: 'flex-end' }}>
                            {a.locked_until && (
                              <button type="button" className="btn btn-ghost btn-sm" disabled={unlock.isPending} onClick={() => unlock.mutate(a.id)}>Unlock</button>
                            )}
                            {!a.is_you && (
                              <>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPending({ kind: 'reset', admin: a })}>Reset password</button>
                                <button type="button" className={`btn btn-ghost btn-sm${a.enabled ? ' is-danger' : ''}`} onClick={() => setPending({ kind: 'toggle', admin: a })}>
                                  {a.enabled ? 'Disable' : 'Enable'}
                                </button>
                              </>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </QueryState>
        <div className="card-body" style={{ paddingTop: 0 }}><MutationError error={unlock.error} /></div>
      </Section>

      <Section title="Recent changes" meta="newest first" flush>
        <QueryState query={{ ...audit, data: audit.data ? audit.data.pages.flatMap((p) => p.entries) : undefined }}>
          {(entries) => entries.length === 0 ? (
            <div className="empty-state t-body">No changes recorded yet.</div>
          ) : (
            <ul className="attention-list card-body">
              {entries.map((e) => (
                <li key={e.id} className="attention-item">
                  <div className="attention-text">
                    <span className="t-body">
                      <strong>{e.actor_name}</strong> {ACTION_WORDS[e.action] ?? e.action}
                      {e.action !== 'admin.password_changed' && e.target_name && <> <strong>{e.target_name}</strong></>}
                      {e.detail && <span className="muted"> — {e.detail}</span>}
                    </span>
                    <span className="t-caption muted" title={formatTimestamp(e.at)}>{formatWhen(e.at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </QueryState>
        <LoadMore hasMore={!!audit.hasNextPage} loading={audit.isFetchingNextPage} onLoad={() => audit.fetchNextPage()} />
      </Section>

      {adding && <AddAdminDialog onClose={() => setAdding(false)} onCreated={(r) => { setAdding(false); setSecret(r); void refresh(); }} />}

      {pending && (
        <ConfirmDialog
          title={pending.kind === 'reset' ? `Reset ${pending.admin.display_name}'s password?`
            : pending.kind === 'role' ? `Make ${pending.admin.display_name} ${roleLabel(pending.role).toLowerCase()}?`
              : pending.admin.enabled ? `Disable ${pending.admin.display_name}?` : `Enable ${pending.admin.display_name}?`}
          confirmLabel={pending.kind === 'reset' ? 'Reset password' : pending.kind === 'role' ? 'Change role' : pending.admin.enabled ? 'Disable' : 'Enable'}
          destructive={pending.kind === 'reset' || (pending.kind === 'toggle' && pending.admin.enabled)}
          pending={update.isPending}
          onConfirm={() => update.mutate(pending)}
          onCancel={() => { setPending(null); update.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            {pending.kind === 'reset'
              ? 'They are signed out everywhere and get a new temporary password, shown to you once. They must choose their own at the next sign-in.'
              : pending.kind === 'role'
                ? 'They are signed out; the new role applies from their next sign-in (at most 30 minutes for an open page).'
                : pending.admin.enabled
                  ? 'They can no longer sign in, and open sessions end within 30 minutes. Nothing they did is undone.'
                  : 'They can sign in again with their current password.'}
          </p>
          <MutationError error={update.error} />
        </ConfirmDialog>
      )}

      {secret && (
        <RevealOnce title={`Temporary password for ${secret.admin.display_name}`} what="temporary password" secret={secret.temporary_password}
          onDismiss={() => setSecret(null)}>
          <p className="t-caption muted" style={{ margin: 0 }}>
            Send it to {secret.admin.email} over a private channel. They must choose their own password when they first sign in.
          </p>
        </RevealOnce>
      )}
    </RequirePermission>
  );
}

function AddAdminDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (result: AdminWithTemporaryPassword) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('viewer');
  const create = useMutation({ mutationFn: () => api.admins.create({ email: email.trim(), display_name: name.trim(), role }), onSuccess: onCreated });
  const ready = email.includes('@') && name.trim().length > 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) create.mutate();
  };

  return (
    <Modal title="Add an admin" onClose={create.isPending ? undefined : onClose} dismissible={!create.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <button type="submit" form="add-admin" className="btn btn-primary" disabled={!ready || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add admin'}
          </button>
        </>
      }>
      <form id="add-admin" className="stack" onSubmit={submit}>
        <TextField label="Name" value={name} onChange={setName} required />
        <TextField label="E-mail" type="email" value={email} onChange={setEmail} required />
        <Select label="Role" value={role} onChange={setRole} searchable={false} options={ROLES}
          hint="Start with the smallest role that does the job; an owner can change it any time." />
        <p className="t-caption muted" style={{ margin: 0 }}>A temporary password is shown once after adding; they choose their own at first sign-in.</p>
        <MutationError error={create.error} />
      </form>
    </Modal>
  );
}
